import { Component, OnInit, OnDestroy, Input, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormsModule, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateModule ,TranslateService } from '@ngx-translate/core';
import {  NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject, Subscription, takeUntil, firstValueFrom } from 'rxjs';
import { AuthService } from 'src/app/authentication/auth.service';
import { UserCredentialModel, UserCredentialService,AccountService } from 'src/app/services/identity';
import { SecuritySettingService, SecuritySettingModel } from 'src/app/services/organization';
import { CustomValidators } from '../../shared/validators/custom-validators';
import { ToastrService } from 'ngx-toastr';
import { ConstantService } from 'src/app/services/common/constant.service';
import { PasswordCompromisedModal } from 'src/app/my-profile/password-compromised-modal/password-compromised-modal';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-reset-password-modal',
  templateUrl: './reset-password-modal.component.html',
  styleUrls: ['./reset-password-modal.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    TranslateModule, 
    NgbModule
  ]
})
export class ResetPasswordModalComponent implements OnInit, OnDestroy {
  activeModal = inject(NgbActiveModal);
  private fb = inject(FormBuilder);
  private securitySettingService = inject(SecuritySettingService);
  private userCredentialService = inject(UserCredentialService);
  private toastrService = inject(ToastrService);
  private translate = inject(TranslateService);
  private authService = inject(AuthService);
  private constantService = inject(ConstantService);
  private accountService = inject(AccountService);
  private ngbModal = inject(NgbModal);

  // TODO: Skipped for migration because:
  //  This input is used in a control flow expression (e.g. `@if` or `*ngIf`)
  //  and migrating would break narrowing currently.
  @Input() daysRemaining: number;

  updatePasswordForm: FormGroup;
  destroy$ = new Subject<boolean>();
  showCurrentPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;
  passwordMatch = true;

  securitySettings: SecuritySettingModel = {} as SecuritySettingModel;
  userModel: UserCredentialModel = {};

  ngOnInit(): void {
    this.updatePasswordForm = this.fb.group({
      currentPassword: [null, Validators.required],
      newPassword: [null, Validators.required],
      confirmPassword: [null, Validators.required],
    });

    this.securitySettingService.apiSecuritySettingSettingsbyorgidGet(0).subscribe(data => {
      this.securitySettings = data;
      const validators = [];

      if (data.pwdMustIncludeNumber) validators.push(CustomValidators.patternValidator(/\d/, { hasNumber: true }));
      if (data.pwdMustIncludeUppercase) validators.push(CustomValidators.patternValidator(/[A-Z]/, { hasCapitalCase: true }));
      if (data.pwdMustIncludeLowercase) validators.push(CustomValidators.patternValidator(/[a-z]/, { hasSmallCase: true }));
      if (data.pwdMustIncludeSpecialCharacter) validators.push(CustomValidators.patternValidator(/[!@#$%^&*(),.?":{}|<>]/, { hasSpecialCharacters: true }));
      validators.push(CustomValidators.patternValidator(/^[^\\/]*$/, {noExcludedCharacters: true, }));
      if (data.passwordMinLength) validators.push(Validators.minLength(data.passwordMinLength));

      validators.push(Validators.required);
      this.updatePasswordForm.controls.newPassword.setValidators(validators);
    });

    this.newPassword.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.checkConfirmPassword());
    this.confirmPassword.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.checkConfirmPassword());
  }

  get currentPassword(): FormControl {
    return this.updatePasswordForm.get('currentPassword') as FormControl;
  }

  get newPassword(): FormControl {
    return this.updatePasswordForm.get('newPassword') as FormControl;
  }

  get confirmPassword(): FormControl {
    return this.updatePasswordForm.get('confirmPassword') as FormControl;
  }

  get passwordControl(): FormControl {
    return this.updatePasswordForm.get('newPassword') as FormControl;
  }

  checkConfirmPassword() {
    if (this.newPassword.value !== this.confirmPassword.value) {
      this.passwordMatch = false;
      this.confirmPassword.setErrors({ passwordNotMatch: true });
    } else {
      this.passwordMatch = true;
      this.confirmPassword.setErrors(null);
    }
  }

  async savePassword() {
    this.userModel.password = this.currentPassword.value;
    this.userModel.newPassword = this.newPassword.value;
    this.userModel.ignorePasswordHistory = false;

    if (!(await this.checkPasswordCompromised())) return;

    this.userCredentialService.apiUserCredentialChangeUserPasswordPost(this.userModel).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (result) => {
        if (result.status) {
          this.toastrService.success(this.translate.instant('Action.Success'));
          this.activeModal.close(true); 
        } else {
          if (result.message === "Error.Password.PasswordComplexityError") {
            result.value.split(/\r?\n/).forEach(msg =>
              this.toastrService.error(
                this.translate.instant(msg),
                null,
                this.constantService.ToastError
              )
            );
          } else if (result.message === "Error.Password.PasswordLastUsedError") {
            this.toastrService.error(
              this.translate.instant(result.message, {
                value: this.securitySettings.enforcePwdHistoryCount,
              }),
              null,
              this.constantService.ToastError
            );
          } else {
            this.toastrService.error(
              this.translate.instant(result.message, {
                value: result.value,
              }),
              null,
              this.constantService.ToastError
            );
          }
        }
      }
    });
  }

  async checkPasswordCompromised(): Promise<boolean> {
    const data = await firstValueFrom(
      this.accountService.apiAccountCheckPasswordCompromisedPost({
        credential: { type: 'Password', userInput: this.newPassword.value }
      })
    );

    if (data === true) {
      const modalRef = this.ngbModal.open(PasswordCompromisedModal, { size: 'lg' });
      return await modalRef.result;
    }

    return true;
  }

  cancel(): void {
    this.activeModal.close(false);
  }

  remindMeLater(): void {
    this.activeModal.dismiss('remind-later');
  }

  showPassword(id: string): void {
    const el = document.getElementById(id) as HTMLInputElement;
    el.type = el.type === 'password' ? 'text' : 'password';
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}